import {
  OrganizationEntity,
  UserDetailsResponseDtoAppFeaturesItem,
  UserEntity,
} from '@/generated/nabisNYWebAPI.schemas';
import { appUserControllerUserDetails } from '@/generated/users/users';
import { useAuthToken } from '@/modules/Auth';
import Loading from '@/modules/loading';
import { useOrganizationContext } from '@/modules/organization';
import { envEnum } from '@/types/env.enum';
import React, { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useAnalytics } from 'use-analytics';
import { useAppScope } from '../app-scope/context/use-app-scope';
import { OrganizationFeatureMap } from './types/user-types';
import { getFeaturesFromUserDetailsResponse } from './utils/get-features-from-user-details-response';
import { getHasAllAppFeatures } from './utils/get-has-all-app-features';
import { getHasAllFeaturesForOrganization } from './utils/get-has-all-features-for-organization';

export const UserContext = React.createContext<{
  appFeatures: UserDetailsResponseDtoAppFeaturesItem[];
  clearUser: () => void;
  isAuthenticated: boolean;
  organization?: OrganizationEntity;
  organizationFeatures: OrganizationFeatureMap;
  organizationId?: string;
  organizations: OrganizationEntity[];
  setUser: (user: UserEntity) => void;
  user?: UserEntity | null;
}>({
  appFeatures: [],
  clearUser: () => void 0,
  isAuthenticated: false,
  organizationFeatures: new Map(),
  organizationId: undefined,
  organizations: [],
  setUser: () => void 0,
});

function useUserContext() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within UserProvider');
  }
  return context;
}

export const UserProvider = (props: React.PropsWithChildren<unknown>) => {
  const { organizationId } = useOrganizationContext();
  const [appFeatures, setAppFeatures] = useState<UserDetailsResponseDtoAppFeaturesItem[]>([]);
  const [organizationFeatures, setOrganizationFeatures] = useState<OrganizationFeatureMap>(new Map());
  const [organizations, setOrganizations] = useState<OrganizationEntity[]>([]);
  const [user, setUser] = useState<UserEntity | null>(null);
  const { token, isAuthenticated, signOut } = useAuthToken();
  const { identify } = useAnalytics();
  const { appScope } = useAppScope();
  const { boot } = useIntercom();

  const clearUser = () => {
    signOut();
    setUser(null);
    setAppFeatures([]);
    setOrganizationFeatures(new Map());
  };

  useEffect(() => {
    async function fetchAppUserData() {
      const res = await appUserControllerUserDetails();

      if (!res.data) {
        throw new Error('Could not retrieve user details');
      }

      const { featuresForOrganizationsMap, appFeatures } = getFeaturesFromUserDetailsResponse(res);

      setUser(res.data.user);
      setOrganizations(res.data.organizations);
      setAppFeatures(appFeatures);
      setOrganizationFeatures(featuresForOrganizationsMap);
    }

    if (isAuthenticated) {
      void fetchAppUserData();
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (user) {
      void identify(user.id, {
        $email: user.email, // mixpanel default email
        $name: `${user.firstName} ${user.lastName}`, // mixpanel default name
        ...user,
        appFeatures,
        organizationFeatures: Object.fromEntries(organizationFeatures.entries()),
        appScope,
      });
    }
  }, [user, identify, appFeatures, organizationFeatures, appScope]);

  useEffect(() => {
    if (user) {
      // Intercom booting
      if ([envEnum.staging, envEnum.production].includes(process.env.NEXT_PUBLIC_ENV as envEnum)) {
        boot({
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          phone: user.phone,
          userId: user.id,
        });
      }
    }
  }, [user, boot]);

  return (
    <UserContext.Provider
      value={{
        user,
        isAuthenticated,
        clearUser,
        setUser,
        appFeatures,
        organizationFeatures,
        organizationId,
        organizations,
      }}
    >
      {!isAuthenticated ? <Loading /> : props.children}
    </UserContext.Provider>
  );
};

export function useMe() {
  const {
    isAuthenticated,
    user,
    clearUser,
    setUser,
    appFeatures,
    organizationFeatures,
    organizationId,
    organizations,
  } = useUserContext();

  return {
    clearUser,
    hasAllAppFeatures: getHasAllAppFeatures(appFeatures),
    hasAllFeaturesForOrganization: getHasAllFeaturesForOrganization(organizationFeatures, organizationId),
    isAuthenticated,
    organizations,
    setUser,
    user: user,
  };
}
